'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '@/lib/i18n/context';

// ─── Constants ────────────────────────────────────────────────────────────────

const POPUP_DELAY_MS     = 10_000;   // hiện sau 10s
const PING_INTERVAL_MS   = 5 * 60_000; // ping server mỗi 5 phút
const DISMISS_KEY        = 'push-prompt-dismissed'; // localStorage key
const SUBSCRIBE_KEY      = 'push-subscribed';       // localStorage key

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : '';
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'idle' | 'visible' | 'loading' | 'success' | 'denied' | 'error';

// ─── Component ────────────────────────────────────────────────────────────────

export function PushNotificationPrompt() {
  const [step, setStep]           = useState<Step>('idle');
  const [closeable, setCloseable] = useState(false);
  const pingTimerRef              = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupTimerRef             = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeableTimerRef         = useRef<ReturnType<typeof setTimeout> | null>(null);

  const vapidKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? '';

  const { locale, setLocale, t } = useI18n();

  // ── Ping server để cập nhật last_seen_at ─────────────────────────────────
  const pingServer = useCallback(async (endpoint: string) => {
    if (!endpoint) return;
    try {
      // Đọc cookie phía client – cookies() là server-only API, không dùng được ở đây
      const postViewed = document.cookie
        .split('; ')
        .find(r => r.startsWith('post_viewed='))
        ?.split('=')[1] ?? '';

      await fetch('/api/push/ping', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint,
          language: locale ?? '',
          posts: postViewed,
        }),
      });
    } catch {
      // silent fail
    }
  }, [locale]);

  // ── Kiểm tra subscription hiện tại khi mount ─────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    (async () => {
      // Nếu đã dismiss vĩnh viễn → bỏ
      if (localStorage.getItem(DISMISS_KEY) === 'forever') return;

      // Nếu browser đã từ chối permission → thôi
      if (Notification.permission === 'denied') return;

      const reg = await navigator.serviceWorker.ready.catch(() => null);
      if (!reg) return;

      const existing = await reg.pushManager.getSubscription().catch(() => null);

      if (existing) {
        // Đã subscribe → mark local + ping ngay + set interval
        localStorage.setItem(SUBSCRIBE_KEY, '1');
        pingServer(existing.endpoint);
        pingTimerRef.current = setInterval(() => pingServer(existing.endpoint), PING_INTERVAL_MS);
        return;
      }

      // Chưa subscribe – kiểm tra xem user đã dismiss chưa
      const dismissed = localStorage.getItem(DISMISS_KEY);

      // Nếu đã dismiss, chỉ hiện lại sau 24h
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        if (!isNaN(dismissedAt) && Date.now() - dismissedAt < 24 * 60 * 60_000) {
          return;
        }
      }

      // Hiện popup sau 10s
      popupTimerRef.current = setTimeout(() => {
        setStep('visible');
        setCloseable(false);
        // Cho phép đóng sau 3s
        closeableTimerRef.current = setTimeout(() => setCloseable(true), 3000);
      }, POPUP_DELAY_MS);
    })();

    return () => {
      if (popupTimerRef.current)   clearTimeout(popupTimerRef.current);
      if (closeableTimerRef.current) clearTimeout(closeableTimerRef.current);
      if (pingTimerRef.current)    clearInterval(pingTimerRef.current);
    };
  }, [pingServer]);

  // ── Xử lý đăng ký ────────────────────────────────────────────────────────
  const handleSubscribe = async () => {
    if (!vapidKey) {
      console.warn('[Push] NEXT_PUBLIC_VAPID_PUBLIC_KEY chưa được cấu hình');
      setStep('error');
      return;
    }

    setStep('loading');

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStep('denied');
        localStorage.setItem(DISMISS_KEY, 'forever');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const p256dh = subscription.getKey('p256dh');
      const auth   = subscription.getKey('auth');
      if (!p256dh || !auth) throw new Error('Missing subscription keys');


      const deviceInfo = `${navigator.userAgent.slice(0, 200)}`;

      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          myxreqId:   getCookie('MYXREQ_ID'), // liên kết với b_visitor.screens
          endpoint:   subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(p256dh),
            auth:   arrayBufferToBase64(auth),
          },
          deviceInfo,
          language:   locale ?? '',
        }),
      });

      localStorage.setItem(SUBSCRIBE_KEY, '1');
      setStep('success');

      // Bắt đầu ping định kỳ
      const ep = subscription.endpoint;
      pingServer(ep);
      pingTimerRef.current = setInterval(() => pingServer(ep), PING_INTERVAL_MS);

      // Tự đóng popup sau 2.5s
      setTimeout(() => setStep('idle'), 2500);
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      setStep('error');
    }
  };

  // ── Đóng popup ────────────────────────────────────────────────────────────
  const handleDismiss = () => {
    if (!closeable) return;
    setStep('idle');
    // Lưu timestamp dismiss – hiện lại sau 24h
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  // ── Không render gì nếu môi trường không hỗ trợ ──────────────────────────
  if (typeof window !== 'undefined' && !('PushManager' in window)) return null;

  const visible = step === 'visible' || step === 'loading' || step === 'success' || step === 'denied' || step === 'error';

  return (
    <>
      {/* ── Backdrop blur nhẹ ── */}
      {visible && (
        <div
          onClick={handleDismiss}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(2px)',
            transition: 'opacity 0.3s ease',
            opacity: visible ? 1 : 0,
          }}
        />
      )}

      {/* ── Popup card ── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Bật thông báo"
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          left: '50%',
          transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(120%)',
          zIndex: 9999,
          width: 'min(380px, calc(100vw - 2rem))',
          transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #141414 0%, #1a1a1a 100%)',
          border: '1px solid rgba(247,43,114,0.25)',
          borderRadius: '18px',
          padding: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
          position: 'relative',
          overflow: 'hidden',
        }}>

          {/* Glow accent */}
          <div style={{
            position: 'absolute',
            top: '-40px',
            right: '-40px',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(247,43,114,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* ── Nút đóng ── */}
          <button
            onClick={handleDismiss}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.5)',
              border: 'none',
              cursor: closeable ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              transition: 'opacity 0.3s ease, background 0.2s',
              opacity: closeable ? 1 : 0,
              pointerEvents: closeable ? 'auto' : 'none',
              padding: 0,
            }}
          >
            ✕
          </button>

          {/* ── Icon + Title ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, rgba(247,43,114,0.2), rgba(192,38,211,0.2))',
              border: '1px solid rgba(247,43,114,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {step === 'success' ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : step === 'denied' ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f72b72" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#ffffff', lineHeight: 1.3 }}>
                {step === 'success' ? t.subscription.success_tit :
                 step === 'denied'  ? t.subscription.denied_tit :
                 step === 'error'   ? t.subscription.error_tit :
                 t.subscription.title
                }
              </p>
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>
                {step === 'success' ? t.subscription.success_des :
                 step === 'denied'  ? t.subscription.denied_des :
                 step === 'error'   ? t.subscription.error_des :
                 t.subscription.rule
                }
              </p>
            </div>
          </div>

          {/* ── Điểm tính năng ── */}
          {(step === 'visible' || step === 'loading') && (
            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                t.subscription.description,
              ].map((text) => (
                <div key={text} style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', paddingLeft: '2px' }}>
                  {text}
                </div>
              ))}
            </div>
          )}

          {/* ── Nút hành động ── */}
          {(step === 'visible' || step === 'loading') && (
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* <button
                onClick={handleDismiss}
                disabled={!closeable}
                style={{
                  flex: '0 0 auto',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.06)',
                  color: closeable ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontSize: '13px',
                  cursor: closeable ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                }}
              >
                {t.subscription.skip}
              </button> */}

              <button
                onClick={handleSubscribe}
                disabled={step === 'loading'}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '10px',
                  background: step === 'loading'
                    ? 'rgba(247,43,114,0.4)'
                    : 'linear-gradient(135deg, #f72b72, #c026d3)',
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: step === 'loading' ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: step === 'loading' ? 'none' : '0 4px 15px rgba(247,43,114,0.35)',
                }}
              >
                {step === 'loading' ? (
                  <>
                    <Spinner />
                    {t.subscription.processing}
                  </>
                ) : (
                  t.subscription.subscribeButton
                )}
              </button>
            </div>
          )}

          {/* ── Countdown bar (chỉ ở step visible) ── */}
          {step === 'visible' && !closeable && (
            <div style={{
              marginTop: '12px',
              height: '2px',
              borderRadius: '2px',
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                background: 'rgba(247,43,114,0.5)',
                animation: 'shrink-bar 3s linear forwards',
              }} />
            </div>
          )}
        </div>

        {/* Hint text */}
        {(step === 'visible' || step === 'loading') && (
          <p style={{
            textAlign: 'center',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.2)',
            margin: '6px 0 0',
          }}>
            {t.subscription.rule}
          </p>
        )}
      </div>

      {/* Animation style */}
      <style>{`
        @keyframes shrink-bar {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </>
  );
}

// ─── Spinner mini ─────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round"
      style={{ animation: 'spin 0.7s linear infinite' }}
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}